import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';
import {
  fetchHireRatingEvent,
  submitHireRating,
  type HireRatingEvent,
  type HireRatingAnswerDto,
} from '../api/hireRatingApi';
import { useIsMobile } from '../hooks/useIsMobile';

// ─── Fixed question set ───────────────────────────────────────────────
interface HireQuestion {
  key: string;
  text: string;
  type: 'Scale' | 'FreeText';
  optional?: boolean;
  scaleLow?: string;
  scaleHigh?: string;
}

const HIRE_QUESTIONS: HireQuestion[] = [
  {
    key: 'overall_hire_quality',
    text: 'How would you rate your overall satisfaction with this hire?',
    type: 'Scale',
    scaleLow: 'Very dissatisfied',
    scaleHigh: 'Very satisfied',
  },
  {
    key: 'role_fit',
    text: 'How well does this person match the role requirements?',
    type: 'Scale',
    scaleLow: 'Poor fit',
    scaleHigh: 'Excellent fit',
  },
  {
    key: 'candidate_quality',
    text: 'How would you rate the quality of candidates presented to you?',
    type: 'Scale',
    scaleLow: 'Below expectations',
    scaleHigh: 'Exceeded expectations',
  },
  {
    key: 'process_speed',
    text: 'How satisfied were you with the speed of the hiring process?',
    type: 'Scale',
    scaleLow: 'Too slow',
    scaleHigh: 'Very fast',
  },
  {
    key: 'ta_communication',
    text: 'How well did the talent acquisition team communicate with you throughout the process?',
    type: 'Scale',
    scaleLow: 'Poor',
    scaleHigh: 'Excellent',
  },
  {
    key: 'would_use_again',
    text: 'How likely are you to use our internal recruiting team for your next hire?',
    type: 'Scale',
    scaleLow: 'Very unlikely',
    scaleHigh: 'Definitely',
  },
  {
    key: 'open_feedback',
    text: 'What could the hiring process do better? (Optional)',
    type: 'FreeText',
    optional: true,
  },
];

// ─── Sub-components ───────────────────────────────────────────────────

function ScaleTiles({
  questionKey,
  value,
  onChange,
  scaleLow = 'Not at all',
  scaleHigh = 'Completely',
}: {
  questionKey: string;
  value: string;
  onChange: (v: string) => void;
  scaleLow?: string;
  scaleHigh?: string;
}) {
  return (
    <>
      <div className="scale-group" role="radiogroup">
        {[1, 2, 3, 4, 5].map(n => {
          const s = String(n);
          return (
            <label
              key={n}
              className={`scale-tile${value === s ? ' scale-tile--selected' : ''}`}
            >
              <input
                type="radio"
                className="sr-only"
                name={questionKey}
                value={s}
                checked={value === s}
                onChange={() => onChange(s)}
              />
              {n}
            </label>
          );
        })}
      </div>
      <div className="scale-endpoints">
        <span>{scaleLow}</span>
        <span>{scaleHigh}</span>
      </div>
    </>
  );
}

function HireIntroScreen({ event, onStart }: { event: HireRatingEvent; onStart: () => void }) {
  const formattedDate = new Date(event.hireDate).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Use the round's landing page message when available (merge fields already substituted by API).
  const introMessage = event.roundLandingPageMessage ?? null;

  return (
    <div className="wizard-body" style={{ paddingBottom: '6rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        {event.roundCohortPeriod ? (
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
            {event.roundCohortPeriod} — Hire satisfaction review
          </p>
        ) : (
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
            Hire satisfaction rating
          </p>
        )}
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.375rem' }}>
          {event.hireeName}
        </h1>
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          {event.hireeRole} · {event.department} · Started {formattedDate}
        </p>
      </div>

      {introMessage ? (
        <p style={{ lineHeight: 1.7, marginBottom: '1.25rem' }}>{introMessage}</p>
      ) : null}

      <div className="anonymity-banner">
        <strong>How this works</strong>
        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', lineHeight: 1.7 }}>
          <li>Your rating helps Talent Acquisition improve future hiring.</li>
          <li>Your name is recorded alongside your responses (this is not anonymous).</li>
          <li>This link is single-use and will expire after submission.</li>
          <li>{HIRE_QUESTIONS.length} questions · takes about 2 minutes.</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Shared wizard engine ─────────────────────────────────────────────
function HireRatingWizard({
  event,
  accessToken,
  isMobile,
}: {
  event: HireRatingEvent;
  accessToken: string;
  isMobile: boolean;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(isMobile ? -1 : 0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const questions = HIRE_QUESTIONS;
  const total = questions.length;
  const question = step >= 0 ? questions[step] : null;
  const answer = question ? (answers[question.key] ?? '') : '';
  const isLastQuestion = step === total - 1;
  const isOptional = question?.optional ?? false;

  const scrollTop = () => bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const handleAnswer = (v: string) => {
    if (!question) return;
    setAnswers(prev => ({ ...prev, [question.key]: v }));
    setError(null);

    // Auto-advance on mobile after scale selection
    if (isMobile && question.type === 'Scale' && !isLastQuestion) {
      setTimeout(() => { setStep(s => s + 1); scrollTop(); }, 250);
    }
  };

  const goNext = async () => {
    if (step === -1) { setStep(0); scrollTop(); return; }

    if (!isOptional && !answer) {
      setError('Please select an answer to continue.');
      return;
    }
    setError(null);

    if (!isLastQuestion) { setStep(s => s + 1); scrollTop(); return; }

    // Submit
    setSubmitting(true);
    const payload: HireRatingAnswerDto[] = Object.entries(answers).map(([questionKey, value]) => ({
      questionKey,
      value,
    }));
    try {
      await submitHireRating(event.id, { answers: payload }, accessToken);
      navigate('/hire-rating/complete');
    } catch {
      setSubmitting(false);
      setError('Something went wrong. Please try again.');
    }
  };

  const goBack = () => { setError(null); setStep(s => s - 1); scrollTop(); };

  // Desktop: render all questions
  if (!isMobile) {
    return (
      <div className="wizard-root wizard-root--desktop">
        <header className="wizard-header">
          <div className="wizard-header__top">
            <img src="/gridworks-logo.png" alt="GridPulse" className="wizard-logo" />
          </div>
        </header>

        <div className="wizard-body">
          <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
            Hire satisfaction rating
          </p>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>{event.hireeName}</h1>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>
            {event.hireeRole} · {event.department}
          </p>

          <div className="anonymity-banner" style={{ marginBottom: '1.75rem' }}>
            Your name is recorded alongside your responses. Ratings are reviewed by Talent Acquisition
            to improve the hiring process.
          </div>

          {questions.map((q, i) => (
            <div key={q.key} style={{ marginBottom: '1.25rem' }}>
              <div className="question-card">
                <p className="question-text">
                  <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '0.875rem' }}>
                    {i + 1}/{total}&ensp;
                  </span>
                  {q.text}
                </p>
                {q.type === 'Scale' && (
                  <ScaleTiles
                    questionKey={q.key}
                    value={answers[q.key] ?? ''}
                    onChange={v => setAnswers(prev => ({ ...prev, [q.key]: v }))}
                    scaleLow={q.scaleLow}
                    scaleHigh={q.scaleHigh}
                  />
                )}
                {q.type === 'FreeText' && (
                  <textarea
                    className="question-textarea"
                    rows={4}
                    value={answers[q.key] ?? ''}
                    onChange={e => setAnswers(prev => ({ ...prev, [q.key]: e.target.value }))}
                    placeholder="Optional feedback…"
                  />
                )}
              </div>
            </div>
          ))}

          {error && <p role="alert" style={{ color: 'var(--color-error)', marginBottom: '1rem' }}>{error}</p>}

          <div className="wizard-footer">
            <button className="btn btn-primary" onClick={goNext} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit rating'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile: step wizard
  return (
    <div className="wizard-root">
      <header className="wizard-header" style={{ position: 'relative' }}>
        <div className="wizard-header__top">
          <img src="/gridworks-logo.png" alt="GridPulse" className="wizard-logo" />
          {step >= 0 && (
            <span className="wizard-step-label">Question {step + 1} of {total}</span>
          )}
        </div>
        <div className="wizard-progress-track">
          <div
            className="wizard-progress-fill"
            style={{ width: step < 0 ? '0%' : `${Math.round(((step + 1) / total) * 100)}%` }}
          />
        </div>
      </header>

      <div className="wizard-body" ref={bodyRef} style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
        {step === -1 && <HireIntroScreen event={event} onStart={goNext} />}

        {question && (
          <div className="question-card">
            <p className="question-text">{question.text}</p>
            {question.type === 'Scale' && (
              <ScaleTiles
                questionKey={question.key}
                value={answer}
                onChange={handleAnswer}
                scaleLow={question.scaleLow}
                scaleHigh={question.scaleHigh}
              />
            )}
            {question.type === 'FreeText' && (
              <textarea
                className="question-textarea"
                rows={4}
                value={answer}
                onChange={e => handleAnswer(e.target.value)}
                placeholder="Optional feedback…"
              />
            )}
          </div>
        )}

        {error && (
          <p role="alert" style={{ color: 'var(--color-error)', marginTop: '0.75rem', fontSize: '0.9rem' }}>
            {error}
          </p>
        )}
      </div>

      <footer className="wizard-footer">
        {step > -1 && (
          <button className="btn btn-ghost" onClick={goBack} disabled={submitting}>
            ← Back
          </button>
        )}
        <button className="btn btn-primary btn-block" onClick={goNext} disabled={submitting}>
          {submitting ? 'Submitting…' : step === -1 ? 'Get started' : isLastQuestion ? 'Submit rating' : 'Next →'}
        </button>
      </footer>
    </div>
  );
}

// ─── Auth wrapper + data fetching ─────────────────────────────────────
export function HireRating() {
  const { id } = useParams<{ id: string }>();
  const { instance, accounts } = useMsal();
  const isMobile = useIsMobile();

  const [event, setEvent] = useState<HireRatingEvent | null>(null);
  const [accessToken, setAccessToken] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'not_found' | 'forbidden' | 'error'>('loading');

  useEffect(() => {
    if (!id) { setStatus('not_found'); return; }

    const load = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        const data = await fetchHireRatingEvent(id, tokenResponse.accessToken);
        setEvent(data);
        setAccessToken(tokenResponse.accessToken);
        setStatus('ready');
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : '';
        if (msg === 'not_found') setStatus('not_found');
        else if (msg === 'forbidden') setStatus('forbidden');
        else setStatus('error');
      }
    };

    load();
  }, [id, instance, accounts]);

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
      </div>
    );
  }

  if (status !== 'ready' || !event) {
    const msg =
      status === 'not_found' ? 'This hire rating link was not found.'
      : status === 'forbidden' ? "You don't have permission to rate this hire."
      : 'Something went wrong. Please try again later.';

    return (
      <div style={{ maxWidth: 480, margin: '4rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Unavailable</h1>
        <p style={{ color: 'var(--color-text-muted)' }}>{msg}</p>
      </div>
    );
  }

  return <HireRatingWizard event={event} accessToken={accessToken} isMobile={isMobile} />;
}

export function HireRatingComplete() {
  return (
    <div style={{ maxWidth: 560, margin: '4rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Thank you</h1>
      <p style={{ fontSize: '1.1rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
        Your hire satisfaction rating has been recorded. Talent Acquisition reviews these
        to continuously improve the hiring process.
      </p>
    </div>
  );
}
